import React, { useState } from 'react';
import { TabsContent } from '../ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Plus, Edit, Trash2, Upload, Download, FileText, Users, X } from 'lucide-react';
import { formatCurrency, formatDate, useDismissedBanner } from '../../lib/utils';
import { jvAgreementsApi } from '../../lib/api';
import { toast } from 'sonner';
import { SaleScenarioModeler } from './SaleScenarioModeler';

/**
 * Inline dismissible wrapper used inside a `.map()` over JV agreements so each
 * banner gets its own persisted dismiss state (scoped per JV). Putting the
 * hook here keeps Rules-of-Hooks happy (one hook per banner-instance instead
 * of inside the loop body of the parent component).
 *
 * `children` should be a function `(dismiss) => ReactNode` so the inner banner
 * can render its own X button styled to match. This lets us reuse the wrapper
 * for both info/warning banners with different palettes.
 */
function DismissibleBannerScope({ storageKey, children }) {
  const [dismissed, dismiss] = useDismissedBanner(storageKey);
  if (dismissed) return null;
  return children(dismiss);
}

export function JVAgreementsTab({
  jvAgreements, waterfallData, documents, project,
  isOwner, isSuperOwner, isDead,
  onEditJV, onDeleteJV, onAddJV,
  onDocumentUpload, onDocumentDownload, onDocumentDelete,
  docFileInputRef, uploadingDoc,
  loadData,
}) {
  // SOP audit: capital_contributed should equal purchase_price × purchase_split.
  // Real-world deals often differ slightly from this due to HUD prorations,
  // EMD already paid, and seller credits — so we only nudge the admin when
  // the gap is *materially* off (the larger of $5,000 or 5%). Below that,
  // we trust the admin's number.
  const [recomputingJv, setRecomputingJv] = useState(null);  // { jv, suggested }
  const [recomputeBusy, setRecomputeBusy] = useState(false);

  const purchasePrice = project?.purchase_price || 0;
  const suggestedCapitalFor = (jv) => {
    if (!purchasePrice) return null;
    const splitDecimal = jv?.purchase_split ?? 1.0;
    return Math.round(purchasePrice * splitDecimal * 100) / 100;
  };
  const isCapitalMismatched = (jv) => {
    const suggested = suggestedCapitalFor(jv);
    if (suggested === null || suggested <= 0) return false;
    const cur = parseFloat(jv?.capital_contributed) || 0;
    const absDiff = Math.abs(cur - suggested);
    const pctDiff = suggested > 0 ? (absDiff / suggested) : 0;
    // Only flag when gap is *material* — at least $5K AND at least 5%.
    // (Under that threshold, HUD prorations and EMD reality cause normal drift.)
    return absDiff >= 5000 && pctDiff >= 0.05;
  };

  const handleRecomputeConfirm = async () => {
    if (!recomputingJv) return;
    setRecomputeBusy(true);
    try {
      const newCap = recomputingJv.suggested;
      // Build the full payload — backend's PUT requires complete JV body.
      const jv = recomputingJv.jv;
      await jvAgreementsApi.update(jv.id, {
        ...jv,
        project_id: jv.project_id,
        capital_contributed: newCap,
      });
      toast.success(`JV capital updated to ${formatCurrency(newCap)}`);
      setRecomputingJv(null);
      loadData?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update JV');
    } finally {
      setRecomputeBusy(false);
    }
  };

  return (
    <TabsContent value="jv">
      {/* SALE SCENARIO MODELER — quick "what-if" for U/C deals.
          Owners only (the calc reveals investor-cost-basis details). */}
      {isOwner && project?.id && (
        <div className="mb-3">
          <SaleScenarioModeler
            projectId={project.id}
            livePurchasePrice={project?.purchase_price || 0}
            defaultSalePrice={project?.sale_price || project?.estimated_sale_price || 0}
            onFinalized={() => {
              // After Save-as-Official Sale fires, refetch project + JV data
              // so the live capital position and balance-sheet panels reflect
              // the newly persisted sale price + transactions.
              if (typeof loadData === 'function') loadData();
            }}
          />
        </div>
      )}
      <Card className="rounded-sm border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">
              JV Agreements {jvAgreements.length > 0 && `(${jvAgreements.length})`}
            </CardTitle>
            {isOwner && !isDead && (
              <Button
                size="sm"
                onClick={onAddJV}
                className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs"
                data-testid="add-jv-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Investor
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {jvAgreements.length > 0 ? (
            <div className="space-y-6">
              {jvAgreements.map((jv) => {
                const capMismatch = isCapitalMismatched(jv);
                const suggestedCap = suggestedCapitalFor(jv);
                return (
                <div key={jv.id} className="space-y-4">
                  {capMismatch && isOwner && (
                    <DismissibleBannerScope storageKey={`jv-capital-mismatch:${jv.id}`}>
                      {(dismiss) => (
                        <div className="p-3 rounded-sm bg-blue-50 border border-blue-200 flex items-start gap-2"
                             data-testid={`jv-capital-mismatch-banner-${jv.id}`}>
                          <span className="text-blue-600 text-base leading-none">i</span>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-blue-900">Heads-up: JV Capital differs materially from contractual baseline</p>
                            <p className="text-[11px] text-blue-800 mt-0.5">
                              Per SOP, <code>capital_contributed</code> represents the investor&apos;s
                              contractual purchase responsibility — Purchase Price × Purchase Split.
                              {' '}Current: <strong>{formatCurrency(jv.capital_contributed)}</strong>
                              {' '}• Baseline: <strong>{formatCurrency(suggestedCap)}</strong>
                              {' '}({formatCurrency(purchasePrice)} × {(((jv.purchase_split ?? 1) * 100)).toFixed(0)}%).
                              {' '}This is often legitimate (HUD prorations, EMD already paid, seller credits) — but if you
                              intended to enter the contractual amount, click Recompute to align.
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm text-xs border-blue-300 text-blue-800 hover:bg-blue-100"
                            onClick={() => setRecomputingJv({ jv, suggested: suggestedCap })}
                            data-testid={`jv-recompute-btn-${jv.id}`}
                          >
                            Recompute
                          </Button>
                          <button
                            onClick={dismiss}
                            className="text-blue-400 hover:text-blue-700 p-1 flex-shrink-0"
                            aria-label="Dismiss banner"
                            data-testid={`dismiss-jv-capital-mismatch-banner-${jv.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </DismissibleBannerScope>
                  )}
                  <div className="flex items-start justify-between">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Investor</p>
                        <p className="font-medium">{jv.investor_name}</p>
                        {jv.investor_email && (
                          <p className="text-xs text-muted-foreground">{jv.investor_email}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Capital Contributed</p>
                        <p className="font-mono font-medium">{formatCurrency(jv.capital_contributed)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Agreement Status</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {(!jv.signing_status || jv.signing_status === 'pending_signature') && (
                            <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`jv-status-${jv.id}`}>Pending Signature</Badge>
                          )}
                          {jv.signing_status === 'sent' && (
                            <Badge className="bg-blue-100 text-blue-800 text-[10px]" data-testid={`jv-status-${jv.id}`}>Sent for Signature</Badge>
                          )}
                          {jv.signing_status === 'signed' && (
                            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`jv-status-${jv.id}`}>Signed</Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        {jv.signing_status === 'signed' && jv.signed_document_url ? (
                          <>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Signed Document</p>
                            <a
                              href={jv.signed_document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                              data-testid={`jv-signed-doc-${jv.id}`}
                            >
                              <FileText className="w-3 h-3" /> {jv.signed_document_filename || 'View Document'}
                            </a>
                          </>
                        ) : isOwner && !isDead ? (
                          <>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Actions</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {(!jv.signing_status || jv.signing_status === 'pending_signature') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-[10px] rounded-sm gap-1 px-2"
                                  onClick={async () => {
                                    try {
                                      await jvAgreementsApi.updateSigningStatus(jv.id, { signing_status: 'sent' });
                                      toast.success('Marked as sent');
                                      loadData();
                                    } catch { toast.error('Failed to update status'); }
                                  }}
                                  data-testid={`jv-mark-sent-${jv.id}`}
                                >
                                  Mark Sent
                                </Button>
                              )}
                              {jv.signing_status === 'sent' && (
                                <>
                                  <input
                                    type="file"
                                    id={`signed-upload-${jv.id}`}
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      try {
                                        toast.info('Uploading signed agreement...');
                                        await jvAgreementsApi.uploadSigned(jv.id, file);
                                        toast.success('Signed agreement uploaded');
                                        loadData();
                                      } catch { toast.error('Failed to upload'); }
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-[10px] rounded-sm gap-1 px-2"
                                    onClick={() => document.getElementById(`signed-upload-${jv.id}`)?.click()}
                                    data-testid={`jv-upload-signed-${jv.id}`}
                                  >
                                    <Upload className="w-3 h-3" /> Upload Signed
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-[10px] rounded-sm gap-1 px-2"
                                    onClick={async () => {
                                      try {
                                        await jvAgreementsApi.updateSigningStatus(jv.id, { signing_status: 'signed' });
                                        toast.success('Marked as signed');
                                        loadData();
                                      } catch { toast.error('Failed to update status'); }
                                    }}
                                    data-testid={`jv-mark-signed-${jv.id}`}
                                  >
                                    Mark Signed
                                  </Button>
                                </>
                              )}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {isOwner && !isDead && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEditJV(jv)}
                          className="rounded-sm"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        {isSuperOwner && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="rounded-sm text-red-600 hover:bg-red-50">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-sm">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                                <Trash2 className="w-5 h-5" />
                                Delete JV Agreement
                              </AlertDialogTitle>
                              <AlertDialogDescription className="space-y-3">
                                <p>Are you sure you want to delete this JV agreement?</p>
                                <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm">
                                  <p className="font-medium text-red-800 mb-2">Warning: This action is permanent</p>
                                  <ul className="text-red-700 text-xs space-y-1">
                                    <li>&#8226; Investor: <strong>{jv.investor_name}</strong></li>
                                    <li>&#8226; Capital Contributed: <strong>{formatCurrency(jv.capital_contributed)}</strong></li>
                                    <li>&#8226; All waterfall tier configurations will be deleted</li>
                                    <li>&#8226; Investor statements will no longer be available</li>
                                    <li>&#8226; This data cannot be recovered after deletion</li>
                                  </ul>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => onDeleteJV(jv.id)}
                                className="bg-red-600 hover:bg-red-700 rounded-sm"
                              >
                                Yes, Delete JV Agreement
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Waterfall Tiers</p>
                    <div className="grid grid-cols-5 gap-2">
                      <div className="p-2 bg-muted/30 rounded-sm">
                        <p className="text-[10px] text-muted-foreground">Tier 1 (1-{jv.tier_1_cutoff_days}d)</p>
                        <p className="font-mono text-sm font-semibold">{(jv.tier_1_investor_split * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded-sm">
                        <p className="text-[10px] text-muted-foreground">Tier 2 ({jv.tier_1_cutoff_days+1}-{jv.tier_2_cutoff_days}d)</p>
                        <p className="font-mono text-sm font-semibold">{(jv.tier_2_investor_split * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded-sm">
                        <p className="text-[10px] text-muted-foreground">Tier 3 ({jv.tier_2_cutoff_days+1}-{jv.tier_3_cutoff_days}d)</p>
                        <p className="font-mono text-sm font-semibold">{(jv.tier_3_investor_split * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded-sm">
                        <p className="text-[10px] text-muted-foreground">Tier 4 ({jv.tier_3_cutoff_days+1}-{jv.tier_4_cutoff_days || 456}d)</p>
                        <p className="font-mono text-sm font-semibold">{((jv.tier_4_investor_split || 0.55) * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded-sm">
                        <p className="text-[10px] text-muted-foreground">Tier 5 ({(jv.tier_4_cutoff_days || 456)+1}+d)</p>
                        <p className="font-mono text-sm font-semibold">{((jv.tier_5_investor_split || 0.60) * 100).toFixed(0)}%</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4" data-testid={`jv-bucket-splits-${jv.id}`}>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Expense Responsibility (% Investor)</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { key: 'purchase_split', label: 'Purchase' },
                        { key: 'closing_split', label: 'Closing' },
                        { key: 'dd_split', label: 'Due Dil.' },
                        { key: 'improvements_split', label: 'Improv.' },
                      ].map(b => (
                        <div key={b.key} className="p-2 bg-muted/30 rounded-sm">
                          <p className="text-[10px] text-muted-foreground">{b.label}</p>
                          <p className="font-mono text-sm font-semibold">{(((jv[b.key] ?? 1) * 100)).toFixed(0)}%</p>
                        </div>
                      ))}
                    </div>
                    {/* Banner: legacy JVs default closing_split to 100%, but per
                        SOP it should be 0% (Manager's responsibility). */}
                    {(jv.closing_split ?? 1) === 1 && isOwner && !isDead && (
                      <DismissibleBannerScope storageKey={`jv-closing-split-warning:${jv.id}`}>
                        {(dismiss) => (
                          <div className="mt-3 p-3 rounded-sm bg-amber-50 border border-amber-200 flex items-start gap-2"
                               data-testid={`jv-closing-split-warning-${jv.id}`}>
                            <span className="text-amber-600 text-base leading-none">⚠</span>
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-amber-900">Review JV closing-split per SOP</p>
                              <p className="text-[11px] text-amber-800 mt-0.5">
                                Per the Land Finder JV Financial SOP, Purchase Closing Costs (Legal/Title) are
                                100% Manager responsibility (Closing should be 0%). This JV is set to 100%
                                investor responsibility — likely a legacy default. Click &quot;Edit&quot; to update,
                                or leave it if this JV genuinely has a non-standard arrangement.
                              </p>
                            </div>
                            <button
                              onClick={dismiss}
                              className="text-amber-500 hover:text-amber-700 p-1 flex-shrink-0"
                              aria-label="Dismiss banner"
                              data-testid={`dismiss-jv-closing-split-warning-${jv.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </DismissibleBannerScope>
                    )}
                  </div>
                </div>
                );
              })}

              {/* ── PER-INVESTOR SPLIT (pooled JV co-investors) ───────────
                  Distributes the JV's investor profit among the named
                  co_investors by each one's funded share (fallback committed).
                  Owner sees every investor; an investor sees only their slice. */}
              {waterfallData?.per_investor_split?.length > 0 && (
                <div className="border-t pt-4" data-testid="per-investor-split-card">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                    Per-Investor Split {waterfallData.is_sold ? '(at sale)' : '(projected — updates at sale)'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {waterfallData.per_investor_split.map((inv) => (
                      <div
                        key={inv.id}
                        className="rounded-sm border border-emerald-100 bg-emerald-50/40 p-3"
                        data-testid={`per-investor-split-${inv.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-800">{inv.name}</p>
                          <span className="text-[11px] text-muted-foreground">{inv.funded_pct}%</span>
                        </div>
                        <div className="mt-2 space-y-1 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Funded</span>
                            <span className="font-mono text-slate-700">{formatCurrency(inv.funded || inv.capital_committed)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Profit share</span>
                            <span className="font-mono font-semibold text-emerald-700" data-testid={`per-investor-profit-${inv.id}`}>
                              {formatCurrency(inv.allocated_profit)}
                            </span>
                          </div>
                          {waterfallData.is_sold && (
                            <div className="flex items-center justify-between border-t border-emerald-100 pt-1">
                              <span className="text-muted-foreground">Total return</span>
                              <span className="font-mono font-semibold text-slate-800">{formatCurrency(inv.total_return)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Pooled JV profit allocated by each investor&apos;s{' '}
                    {waterfallData.per_investor_split[0]?.basis === 'committed' ? 'committed capital' : 'funded'} share.
                  </p>
                </div>
              )}


              {waterfallData && (
                <div className="border-t pt-4 space-y-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Capital Position {waterfallData.is_sold ? '(at sale)' : '(active deal — net positions update with every txn)'}
                  </p>

                  {/* ── BALANCE-SHEET MODEL (Wave 2) ───────────────────────
                      4 cards in a 2×2 grid: each party gets a Cash In card
                      (what they actually paid) and a Net Owed card (what
                      they still owe per JV split %). Negative Net = overpaid
                      = will be reimbursed at sale.

                      Reconciliation strip below proves: Investor Resp + LF
                      Resp = Total Cost Basis (penny exact). When Cash In
                      doesn't reconcile to Cost Basis, the variance surfaces
                      a data-entry gap the user can hunt down. */}
                  {waterfallData.balance_sheet ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="capital-position-summary">
                        {/* CASH IN — Investor (Capital Flow Ledger) */}
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                          <p className="text-[10px] uppercase tracking-wide text-emerald-700">Cash In by Investor</p>
                          <p className="text-[9px] text-emerald-700/70 -mt-0.5 mb-0.5 italic">Capital Flow Ledger</p>
                          <p className="font-mono text-base font-semibold text-emerald-700" data-testid="bs-investor-cash-in">
                            {formatCurrency(waterfallData.balance_sheet.investor_cash_in || 0)}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Wires + investor-paid txns
                            {(waterfallData.balance_sheet.reimbursements_to_investor || 0) > 0 && (
                              <> − {formatCurrency(waterfallData.balance_sheet.reimbursements_to_investor)} reimbs received</>
                            )}
                          </p>
                        </div>

                        {/* CASH IN — Land Finder (Capital Flow Ledger) */}
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                          <p className="text-[10px] uppercase tracking-wide text-emerald-700">Cash In by Land Finder</p>
                          <p className="text-[9px] text-emerald-700/70 -mt-0.5 mb-0.5 italic">Capital Flow Ledger</p>
                          <p className="font-mono text-base font-semibold text-emerald-700" data-testid="bs-lf-cash-in">
                            {formatCurrency(waterfallData.balance_sheet.lf_cash_in || 0)}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            EMD + LF-paid txns
                            {(waterfallData.balance_sheet.reimbursements_to_lf || 0) > 0 && (
                              <> − {formatCurrency(waterfallData.balance_sheet.reimbursements_to_lf)} reimbs received</>
                            )}
                          </p>
                        </div>

                        {/* NET PARTNER POSITION — Investor */}
                        {(() => {
                          const net = waterfallData.balance_sheet.investor_net_owed || 0;
                          const owesMoney = net > 0.005;     // positive: investor still owes
                          const isOwed = net < -0.005;       // negative: investor overpaid
                          const cls = owesMoney
                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                            : isOwed
                            ? 'bg-blue-50 border-blue-200 text-blue-800'
                            : 'bg-slate-50 border-slate-200 text-slate-700';
                          return (
                            <div className={`p-3 border rounded-sm ${cls}`}>
                              <p className="text-[10px] uppercase tracking-wide">
                                Investor Net Partner Position
                              </p>
                              <p className="text-[9px] opacity-70 -mt-0.5 mb-0.5 italic">
                                {owesMoney ? 'Still owes at settlement' : isOwed ? 'Owed back at settlement' : 'Reconciled'}
                              </p>
                              <p className="font-mono text-base font-semibold" data-testid="bs-investor-net-owed">
                                {formatCurrency(Math.abs(net))}
                              </p>
                              <p className="text-[10px] mt-0.5 opacity-80">
                                Hurdle {formatCurrency(waterfallData.balance_sheet.investor_responsibility || 0)}
                                {' '}− Cash {formatCurrency(waterfallData.balance_sheet.investor_cash_in || 0)}
                              </p>
                            </div>
                          );
                        })()}

                        {/* NET PARTNER POSITION — Land Finder */}
                        {(() => {
                          const net = waterfallData.balance_sheet.lf_net_owed || 0;
                          const owesMoney = net > 0.005;     // positive: LF still owes
                          const isOwed = net < -0.005;       // negative: LF overpaid (gets mgr reimb)
                          const cls = owesMoney
                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                            : isOwed
                            ? 'bg-blue-50 border-blue-200 text-blue-800'
                            : 'bg-slate-50 border-slate-200 text-slate-700';
                          return (
                            <div className={`p-3 border rounded-sm ${cls}`}>
                              <p className="text-[10px] uppercase tracking-wide">
                                LF Net Partner Position
                              </p>
                              <p className="text-[9px] opacity-70 -mt-0.5 mb-0.5 italic">
                                {owesMoney ? 'Still owes at settlement' : isOwed ? 'Owed back at settlement' : 'Reconciled'}
                              </p>
                              <p className="font-mono text-base font-semibold" data-testid="bs-lf-net-owed">
                                {formatCurrency(Math.abs(net))}
                              </p>
                              <p className="text-[10px] mt-0.5 opacity-80">
                                Hurdle {formatCurrency(waterfallData.balance_sheet.lf_responsibility || 0)}
                                {' '}− Cash {formatCurrency(waterfallData.balance_sheet.lf_cash_in || 0)}
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      {/* NET PARTNER POSITION dual-ledger summary table.
                          Surfaces the SOP-recommended 3-row dual-ledger
                          reconciliation explicitly: Contractual Hurdle (Legal
                          Mandate Ledger) | Cash Paid (Capital Flow Ledger) |
                          Net Position. Both parties side-by-side. */}
                      {(() => {
                        const bs = waterfallData.balance_sheet;
                        const investorNet = bs.investor_net_owed || 0;
                        const lfNet = bs.lf_net_owed || 0;
                        const netCls = (n) => n > 0.005 ? 'text-amber-700' : n < -0.005 ? 'text-blue-700' : 'text-slate-600';
                        const netLabel = (n) => n > 0.005 ? 'Owes' : n < -0.005 ? 'Owed back' : 'Square';
                        return (
                          <div className="rounded-sm border border-slate-200 overflow-hidden" data-testid="net-partner-position-panel">
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                              <p className="text-[11px] uppercase tracking-wide text-slate-700 font-semibold">
                                Net Partner Position — Dual-Ledger Reconciliation
                              </p>
                              <p className="text-[10px] text-muted-foreground italic">
                                Contractual Hurdle (Legal Mandate) vs Cash Paid (Capital Flow)
                              </p>
                            </div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left border-b border-slate-200 bg-slate-50/40">
                                  <th className="px-3 py-1.5 font-medium text-slate-600">Party</th>
                                  <th className="px-3 py-1.5 font-medium text-slate-600 text-right">Contractual Hurdle</th>
                                  <th className="px-3 py-1.5 font-medium text-slate-600 text-right">Cash Paid</th>
                                  <th className="px-3 py-1.5 font-medium text-slate-600 text-right">Net Position</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                <tr data-testid="npp-row-investor">
                                  <td className="px-3 py-2 font-medium text-slate-800">Investor</td>
                                  <td className="px-3 py-2 text-right font-mono" data-testid="npp-investor-hurdle">
                                    {formatCurrency(bs.investor_responsibility || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-emerald-700" data-testid="npp-investor-cash">
                                    {formatCurrency(bs.investor_cash_in || 0)}
                                  </td>
                                  <td className={`px-3 py-2 text-right font-mono font-semibold ${netCls(investorNet)}`} data-testid="npp-investor-net">
                                    {formatCurrency(investorNet)}{' '}
                                    <span className="text-[10px] uppercase font-normal opacity-70">{netLabel(investorNet)}</span>
                                  </td>
                                </tr>
                                <tr data-testid="npp-row-lf">
                                  <td className="px-3 py-2 font-medium text-slate-800">Land Finder</td>
                                  <td className="px-3 py-2 text-right font-mono" data-testid="npp-lf-hurdle">
                                    {formatCurrency(bs.lf_responsibility || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-emerald-700" data-testid="npp-lf-cash">
                                    {formatCurrency(bs.lf_cash_in || 0)}
                                  </td>
                                  <td className={`px-3 py-2 text-right font-mono font-semibold ${netCls(lfNet)}`} data-testid="npp-lf-net">
                                    {formatCurrency(lfNet)}{' '}
                                    <span className="text-[10px] uppercase font-normal opacity-70">{netLabel(lfNet)}</span>
                                  </td>
                                </tr>
                                <tr className="bg-slate-50/40 border-t-2 border-slate-200">
                                  <td className="px-3 py-2 font-semibold text-slate-800">Total</td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold" data-testid="npp-total-hurdle">
                                    {formatCurrency(bs.total_cost_basis_check || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700" data-testid="npp-total-cash">
                                    {formatCurrency(bs.total_cash_in || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold text-slate-700" data-testid="npp-total-net">
                                    {formatCurrency((investorNet) + (lfNet))}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            {(bs.lf_emergency_advance || 0) > 0.005 && (
                              <div className="px-3 py-2 bg-rose-50 border-t border-rose-200 text-[11px] text-rose-900">
                                <span className="font-semibold">Senior LF Emergency Advance:</span>{' '}
                                <span className="font-mono font-semibold">{formatCurrency(bs.lf_emergency_advance)}</span>{' '}
                                — outside the hurdle ledger. LF repaid SENIOR to investor hurdle at sale (waterfall Step 2).
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* RECONCILIATION STRIP — proves the books balance.
                          When variance ≠ 0, surfaces missing/extra txns. */}
                      {(() => {
                        const bs = waterfallData.balance_sheet;
                        const variance = bs.cash_basis_variance || 0;
                        const isReconciled = Math.abs(variance) < 0.51;  // half-cent
                        const emergency = bs.lf_emergency_advance || 0;
                        return (
                          <div
                            className={`rounded-sm border px-3 py-2 ${isReconciled ? 'bg-emerald-50/60 border-emerald-200' : 'bg-amber-50/60 border-amber-300'}`}
                            data-testid="balance-sheet-reconciliation"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                              <div>
                                <p className="uppercase tracking-wide text-muted-foreground">Total Contractual Hurdle</p>
                                <p className="font-mono font-semibold text-base" data-testid="bs-total-cost-basis">
                                  {formatCurrency(bs.total_cost_basis_check || 0)}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  = Investor Hurdle {formatCurrency(bs.investor_responsibility || 0)}
                                  {' '}+ LF Hurdle {formatCurrency(bs.lf_responsibility || 0)}
                                </p>
                              </div>
                              <div>
                                <p className="uppercase tracking-wide text-muted-foreground">Total Cash In{emergency > 0 && ' (incl. emergency)'}</p>
                                <p className="font-mono font-semibold text-base">
                                  {formatCurrency(bs.total_cash_in || 0)}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  Investor {formatCurrency(bs.investor_cash_in || 0)}
                                  {' '}+ LF {formatCurrency(bs.lf_cash_in || 0)}
                                  {emergency > 0 && (
                                    <> (LF includes senior emergency {formatCurrency(emergency)})</>
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="uppercase tracking-wide text-muted-foreground">
                                  Variance {isReconciled ? '✓' : '⚠'}
                                </p>
                                <p
                                  className={`font-mono font-semibold text-base ${isReconciled ? 'text-emerald-700' : 'text-amber-700'}`}
                                  data-testid="bs-variance"
                                >
                                  {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  {isReconciled
                                    ? 'Cash flow reconciles to contractual hurdle.'
                                    : 'Possible missing txn. Review the transaction list.'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      Balance-sheet view requires a JV agreement on this project.
                    </p>
                  )}

                  {/* Per-bucket breakdown of contractual hurdle (transparent
                      to admin: shows EXACTLY which expenses contribute to
                      what investor must be repaid). */}
                  {waterfallData.settlement_buckets && Object.keys(waterfallData.settlement_buckets).length > 0 && (
                    <div className="rounded-sm border border-slate-200 bg-slate-50/40" data-testid="hurdle-bucket-breakdown">
                      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-wide text-slate-700 font-semibold">
                          Hurdle Bucket Breakdown
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          (amount × investor split %, by responsibility category)
                        </p>
                      </div>
                      <div className="grid grid-cols-4 divide-x divide-slate-200">
                        {[
                          { key: 'purchase', label: 'Purchase' },
                          { key: 'closing', label: 'Closing' },
                          { key: 'dd', label: 'Due Diligence' },
                          { key: 'improvements', label: 'Improvements' },
                        ].map(({ key, label }) => {
                          const b = waterfallData.settlement_buckets?.[key] || {};
                          const investorResponsibility = (b.manager_advance || 0) + (b.investor_self_paid || 0);
                          const lfPaid = (b.manager_advance || 0) + (b.lf_self_funded || 0) + (b.lf_paid_emd || 0);
                          const invPaid = (b.investor_self_paid || 0) + (b.investor_overpayment || 0);
                          return (
                            <div key={key} className="p-3" data-testid={`bucket-${key}`}>
                              <p className="text-[10px] uppercase tracking-wide text-slate-600">{label}</p>
                              <p className="text-[10px] text-slate-500 mt-1">
                                Investor responsibility:
                                <span className="font-mono font-semibold ml-1 text-slate-700">
                                  {formatCurrency(investorResponsibility)}
                                </span>
                              </p>
                              {lfPaid > 0 && (
                                <p className="text-[10px] text-slate-500">
                                  LF paid: <span className="font-mono">{formatCurrency(lfPaid)}</span>
                                </p>
                              )}
                              {invPaid > 0 && (
                                <p className="text-[10px] text-slate-500">
                                  Inv. paid: <span className="font-mono">{formatCurrency(invPaid)}</span>
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Purchase row note: capital_contributed represents the
                          investor purchase responsibility too. */}
                      {(waterfallData.capital_contributed_on_jv || 0) > 0 && (
                        <div className="px-3 py-2 border-t border-slate-200 text-[10px] text-slate-600">
                          <span className="font-semibold">Note:</span> Investor&apos;s purchase responsibility (capital_contributed){' '}
                          <span className="font-mono">{formatCurrency(waterfallData.capital_contributed_on_jv)}</span>{' '}
                          is added to the hurdle on top of the buckets above.
                        </div>
                      )}
                    </div>
                  )}

                  {(waterfallData.manager_advance > 0 || waterfallData.investor_overpayment > 0 || (waterfallData.lf_self_funded || 0) > 0 || (waterfallData.lf_paid_emd || 0) > 0) && (
                    <div className="p-3 rounded-sm bg-amber-50 border border-amber-200" data-testid="manager-advance-block">
                      <p className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold mb-2">Settlement Detail</p>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-amber-700">LF fronted (DD/closing exp): </span>
                          <span className="font-mono font-medium">{formatCurrency(waterfallData.manager_advance || 0)}</span>
                        </div>
                        <div>
                          <span className="text-amber-700">LF fronted EMD: </span>
                          <span className="font-mono font-medium">{formatCurrency(waterfallData.lf_paid_emd || 0)}</span>
                        </div>
                        <div>
                          <span className="text-amber-700">Investor over-paid LF: </span>
                          <span className="font-mono font-medium">{formatCurrency(waterfallData.investor_overpayment || 0)}</span>
                        </div>
                        <div>
                          <span className="text-amber-700">LF self-funded burden: </span>
                          <span className="font-mono font-medium">{formatCurrency(waterfallData.lf_self_funded || 0)}</span>
                        </div>
                        <div className="col-span-2 pt-2 border-t border-amber-200">
                          <span className="text-amber-900 font-semibold">Net Manager Preferred Reimbursement Owed at sale: </span>
                          <span className="font-mono font-bold text-amber-900">{formatCurrency(waterfallData.manager_reimbursement_owed || 0)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No JV agreement configured. Add one to track investor splits.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents Section */}
      <Card className="rounded-sm border mt-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
              <FileText className="w-4 h-4" />
              JV Agreement Documents
            </CardTitle>
            {isOwner && (
              <>
                <input
                  type="file"
                  ref={docFileInputRef}
                  onChange={onDocumentUpload}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                />
                <Button
                  size="sm"
                  onClick={() => docFileInputRef.current?.click()}
                  disabled={uploadingDoc}
                  className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs"
                  data-testid="upload-doc-btn"
                >
                  {uploadingDoc ? (
                    <span className="animate-spin mr-1">&#9676;</span>
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Upload Document
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-forest-600" />
                    <div>
                      <p className="font-medium text-sm">{doc.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        Uploaded {formatDate(doc.uploaded_at)} &#8226; {(doc.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDocumentDownload(doc)}
                      className="rounded-sm"
                    >
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                    {isOwner && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="rounded-sm text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete &quot;{doc.filename}&quot;? This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => onDocumentDelete(doc.id)}
                              className="bg-red-600 hover:bg-red-700 rounded-sm"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-muted-foreground text-sm">
              {isOwner ? (
                <p>No documents uploaded. Upload signed JV agreements here.</p>
              ) : (
                <p>No JV agreement documents available.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recompute Capital confirmation modal */}
      <AlertDialog open={!!recomputingJv} onOpenChange={(o) => !o && setRecomputingJv(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="jv-recompute-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Recompute JV Capital Contributed</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <span className="block">
                Per the Land Finder JV Financial SOP, an investor&apos;s <code>capital_contributed</code>{' '}
                must equal <strong>Purchase Price × Purchase Split</strong>. Updating this value ensures
                the SOP waterfall returns the correct hurdle, profit shares, and Manager Reimbursement.
              </span>
              {recomputingJv && (
                <span className="block grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-sm">
                  <span className="block">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Current</span>
                    <span className="block font-mono font-semibold">
                      {formatCurrency(recomputingJv.jv?.capital_contributed)}
                    </span>
                  </span>
                  <span className="block">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">After Recompute</span>
                    <span className="block font-mono font-semibold text-emerald-700">
                      {formatCurrency(recomputingJv.suggested)}
                    </span>
                  </span>
                </span>
              )}
              <span className="block text-xs text-muted-foreground">
                This only changes the contractual hurdle on the JV agreement. It does NOT modify any
                transactions, the project&apos;s purchase price, or any signed documents.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={recomputeBusy} data-testid="jv-recompute-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRecomputeConfirm(); }}
              disabled={recomputeBusy}
              className="bg-forest-500 hover:bg-forest-600 text-white"
              data-testid="jv-recompute-confirm"
            >
              {recomputeBusy ? 'Updating…' : 'Update Capital'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContent>
  );
}
